<script setup lang="ts">
import { Button } from './button';
import { Input } from './input';
import { Alert, AlertDescription, AlertTitle } from './alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './tabs';
import { type Ref, ref, computed, onMounted, onBeforeUnmount, useTemplateRef } from 'vue';
import { auth } from './firebase';
import {
  GoogleAuthProvider,
  SAMLAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  createUserWithEmailAndPassword,
  updateProfile,
  type UserCredential,
} from 'firebase/auth';
import { useRoute } from 'vue-router';
import { crossAppActions } from './cross-app-actions.ts';
import { alertModal } from './modals/AlertModal.vue';
import type { ActionCodeSettings } from '@firebase/auth';
import LoginOrbit from './LoginOrbit.vue';
import { platformToolForHostname, type SpriteRegion } from './login-platform';
import OrbitSprites from './OrbitSprites.vue';
import toolkitLogo from '../tri-frontend/assets/toolkit-logo-only.svg';

const activeTool = platformToolForHostname(window.location.hostname);
const showPassword = ref(false);
const authTab = ref('login');
const platform = useTemplateRef<HTMLElement>('platform');
const orbitBounds = ref<SpriteRegion | null>(null);
const highlightedIcon = ref<SpriteRegion | null>(null);
const spriteExclusions = ref<SpriteRegion[]>([]);
const spriteTarget = computed(() => {
  const icon = highlightedIcon.value;
  const orbit = orbitBounds.value;
  if (!icon || !orbit) return null;
  return {
    x: orbit.x + icon.x * orbit.width,
    y: orbit.y + icon.y * orbit.height,
    width: icon.width * orbit.width,
    height: icon.height * orbit.height,
  };
});
let sceneObserver: ResizeObserver | null = null;
let sceneMeasure: (() => void) | null = null;
onMounted(() => {
  const root = platform.value;
  const artwork = root?.querySelector('.orbit-artwork');
  if (!root || !artwork) return;
  sceneMeasure = () => {
    const page = root.getBoundingClientRect();
    const scene = artwork.getBoundingClientRect();
    orbitBounds.value =
      scene.width && scene.height
        ? {
            x: (scene.left - page.left) / page.width,
            y: (scene.top - page.top) / page.height,
            width: scene.width / page.width,
            height: scene.height / page.height,
          }
        : null;
    spriteExclusions.value = Array.from(root.querySelectorAll('[data-sprite-exclusion]')).flatMap((label) => {
      const rect = label.getBoundingClientRect();
      if (!rect.width || !rect.height) return [];
      return [
        {
          x: (rect.left - page.left - 12) / page.width,
          y: (rect.top - page.top - 12) / page.height,
          width: (rect.width + 24) / page.width,
          height: (rect.height + 24) / page.height,
        },
      ];
    });
  };
  sceneObserver = new ResizeObserver(sceneMeasure);
  sceneObserver.observe(root);
  sceneObserver.observe(artwork);
  root.querySelectorAll('[data-sprite-exclusion]').forEach((label) => sceneObserver?.observe(label));
  root.addEventListener('animationend', sceneMeasure);
});
onBeforeUnmount(() => {
  sceneObserver?.disconnect();
  if (sceneMeasure) platform.value?.removeEventListener('animationend', sceneMeasure);
});

const route = useRoute();
const awaitingLogin = ref(false as false | 'google' | 'oldcastle' | 'wieland' | 'password' | 'register');
const loginError = ref('');
const email = ref('');
const password = ref('');
const phase = ref('email' as 'email' | 'auth');

// Registration form fields
const registerName = ref('');
const registerEmail = ref('');
const registerPassword = ref('');
const registerConfirmPassword = ref('');
const registerError = ref('');
const resettingPassword = ref(false);

function oldcastleAuth() {
  return login('oldcastle', loginError, () => signInWithPopup(auth, new SAMLAuthProvider('saml.oldcastle')));
}

function wielandAuth() {
  return login('wieland', loginError, () => signInWithPopup(auth, new SAMLAuthProvider('saml.wieland')));
}

function googleAuth(type: 'login' | 'register') {
  return login('google', loginError, async () => {
    const userCredentials = await signInWithPopup(auth, new GoogleAuthProvider());

    if (type === 'register') {
      await crossAppActions.users.createDemoTenantIfNecessary();
    }

    return userCredentials;
  });
}

function emailAuth() {
  if (!email.value.trim() || !email.value.includes('@')) {
    return;
  }

  if (phase.value === 'email') {
    const emailLower = email.value.toLowerCase();
    if (/(inwesco\.com|nationalpipe\.com|diamondcp\.biz|oldcastle\.com)/.test(emailLower)) {
      oldcastleAuth();
    } else if (/wieland\.com/.test(emailLower) && DEPLOYMENT_ENVIRONMENT === 'prod') {
      wielandAuth();
    } else if (emailLower.includes('@tyler.church') || emailLower.includes('@jhjplc.com')) {
      googleAuth('login');
    } else {
      phase.value = 'auth';
    }

    return;
  }

  if (!password.value.trim()) {
    return;
  }

  return login('password', loginError, () => signInWithEmailAndPassword(auth, email.value, password.value));
}

const loadingMessage = ref('');

async function login(
  type: 'google' | 'oldcastle' | 'wieland' | 'password' | 'register',
  errorRef: Ref<string>,
  action: () => Promise<UserCredential>,
  canRetry = true,
): Promise<void> {
  try {
    awaitingLogin.value = type;
    errorRef.value = '';

    loadingMessage.value =
      type === 'google'
        ? 'Connecting to Google...'
        : type === 'oldcastle' || type === 'wieland'
          ? 'Redirecting to company login...'
          : type === 'password'
            ? 'Verifying credentials...'
            : 'Creating your account...';

    const userCredentials = await action();
    const idToken = await userCredentials.user.getIdToken();
    await fetch('/api/crossapp/set-session-cookie', {
      method: 'POST',
      body: idToken,
    });

    if (FIREBASE_USING_EMULATORS) {
      await crossAppActions.users.ensureAccessOnTestSite();
    }

    loadingMessage.value = 'Redirecting...';
    const returnTo = route.query.returnTo;
    if (typeof returnTo === 'string' && returnTo !== '/404' && returnTo !== '/no-access') {
      window.location.href = returnTo;
    } else {
      window.location.href = '/';
    }
  } catch (e: any) {
    console.error(e);
    const errorCode = e.code;
    if (errorCode === 'auth/popup-closed-by-user') {
      return;
    }

    if (errorCode === 'auth/user-not-found') {
      await alertModal({
        title: 'User Not Found',
        message: 'We could not locate you in the system.',
      });
      return;
    }

    if (
      errorCode === 'auth/internal-error' &&
      typeof e.message === 'string' &&
      e.message.includes('deadline exceeded') &&
      canRetry
    ) {
      console.info(e, 'beforeUserSignedIn likely took too long, trying again.');
      return login(type, errorRef, action, false);
    }

    errorRef.value = `We were unable to log you in. Please check your credentials and try again.`;
    if (errorCode !== 'auth/invalid-credential') {
      const errorMessage = e.message;
      errorRef.value += `\n\nError was: ${errorMessage}`;
    }
  } finally {
    awaitingLogin.value = false;
  }
}

async function passwordReset() {
  if (!email.value) {
    await alertModal({
      title: 'Need Email Address',
      message: 'Please enter your email address before requesting a password reset.',
    });
    return;
  }

  try {
    resettingPassword.value = true;

    let url = window.location.origin;
    const returnTo = route.query.returnTo;
    if (typeof returnTo === 'string' && returnTo !== '/404' && returnTo !== '/no-access' && returnTo.startsWith('/')) {
      url += returnTo;
    }
    const settings: ActionCodeSettings = {
      url,
    };
    if (window.location.host.includes('envirotoolkit.com')) {
      settings.linkDomain = window.location.host;
    }

    await sendPasswordResetEmail(auth, email.value, settings);
    await alertModal({
      title: 'Password Reset Email Sent',
      message: `A password reset email has been sent to ${email.value}. Please check your inbox and follow the instructions.`,
    });
  } catch (error: any) {
    await alertModal({
      title: 'Password Reset Failed',
      message: `We couldn't send the password reset email. Please try again. Error: ${error.message}`,
    });
  } finally {
    resettingPassword.value = false;
  }
}

async function registerUser() {
  registerError.value = '';

  if (registerPassword.value !== registerConfirmPassword.value) {
    registerError.value = 'Passwords do not match';
    return;
  }

  return login('register', registerError, async () => {
    const userCredential = await createUserWithEmailAndPassword(auth, registerEmail.value, registerPassword.value);

    await updateProfile(userCredential.user, {
      displayName: registerName.value,
    });

    await crossAppActions.users.createDemoTenantIfNecessary();

    await crossAppActions.users.youUpdate({ name: registerName.value });

    return userCredential;
  });
}
</script>

<template>
  <main ref="platform" class="platform-login">
    <OrbitSprites :target="spriteTarget" :orbit="orbitBounds" :exclusions="spriteExclusions" />
    <header class="platform-header">
      <div class="platform-brand">
        <img :src="toolkitLogo" alt="Toolkit" class="toolkit-logo" />
        <h1 class="platform-headline" data-sprite-exclusion>
          <span>Your tools.</span>
          <span>One connected platform.</span>
        </h1>
      </div>
      <a href="https://tritoolkit.com" target="_blank" rel="noopener noreferrer" class="learn-more">
        Learn more <span aria-hidden="true">↗</span>
      </a>
    </header>

    <div class="platform-body">
      <section class="platform-scene" aria-label="Toolkit Platform orbit">
        <LoginOrbit :activeTool="activeTool" @highlight="highlightedIcon = $event" />
      </section>

      <section id="sign-in" class="login-panel" aria-labelledby="login-heading" :aria-busy="!!awaitingLogin">
        <div v-if="awaitingLogin" class="login-loading" role="status" aria-live="polite">
          <div class="loading-spinner" aria-hidden="true"></div>
          <p>{{ loadingMessage }}</p>
        </div>
        <div class="login-panel-header">
          <img v-if="activeTool" :src="activeTool.iconUrl" alt="" class="active-tool-icon" />
          <p class="active-tool-label">{{ activeTool?.label ?? 'Toolkit' }}</p>
          <h2 id="login-heading" data-sprite-exclusion>
            {{ authTab === 'register' ? 'Create your account' : `Sign in to ${activeTool?.label ?? 'Toolkit'}` }}
          </h2>
          <p class="login-subtitle" data-sprite-exclusion>
            {{ authTab === 'register' ? 'Get started with Toolkit.' : 'Access your workspace.' }}
          </p>
        </div>
        <Tabs v-model="authTab" class="login-tabs w-full">
          <TabsList class="grid w-full grid-cols-2 border-white/20 bg-white/10">
            <TabsTrigger
              value="login"
              class="data-[state=active]:text-brand-blue text-white/80 data-[state=active]:bg-white"
              >Sign In</TabsTrigger
            >
            <TabsTrigger
              value="register"
              class="data-[state=active]:text-brand-blue text-white/80 data-[state=active]:bg-white"
              >Register</TabsTrigger
            >
          </TabsList>

          <!-- Register Tab -->
          <TabsContent value="register" class="mt-4 space-y-4">
            <form @submit.prevent="registerUser()">
              <Alert
                v-if="registerError"
                variant="destructive"
                class="mb-4 border-red-500/20 bg-red-900/20 text-red-400"
              >
                <AlertTitle>Registration Error</AlertTitle>
                <AlertDescription>
                  {{ registerError }}
                </AlertDescription>
              </Alert>

              <div class="space-y-4">
                <!-- Name fields row -->
                <div class="relative">
                  <i class="fas fa-user absolute top-1/2 left-3 -translate-y-1/2 transform text-white/60"></i>
                  <Input
                    id="registerName"
                    aria-label="Name"
                    autocomplete="name"
                    type="text"
                    placeholder="Name"
                    :disabled="awaitingLogin"
                    required
                    v-model.trim="registerName"
                    class="border-white/20 bg-white/10 pl-10 text-white placeholder:text-white/60 focus:border-white focus:ring-white/20"
                  />
                </div>

                <!-- Email -->
                <div class="relative">
                  <i class="fas fa-envelope absolute top-1/2 left-3 -translate-y-1/2 transform text-white/60"></i>
                  <Input
                    id="registerEmail"
                    aria-label="Email"
                    autocomplete="email"
                    type="email"
                    placeholder="Email"
                    :disabled="awaitingLogin"
                    required
                    v-model.trim="registerEmail"
                    class="border-white/20 bg-white/10 pl-10 text-white placeholder:text-white/60 focus:border-white focus:ring-white/20"
                  />
                </div>

                <!-- Password -->
                <div class="relative">
                  <i class="fas fa-lock absolute top-1/2 left-3 -translate-y-1/2 transform text-white/60"></i>
                  <Input
                    id="registerPassword"
                    aria-label="Password"
                    autocomplete="new-password"
                    type="password"
                    placeholder="Enter your password"
                    :disabled="awaitingLogin"
                    required
                    minlength="8"
                    v-model="registerPassword"
                    class="border-white/20 bg-white/10 pr-10 pl-10 text-white placeholder:text-white/60 focus:border-white focus:ring-white/20"
                  />
                </div>

                <!-- Confirm Password -->
                <div class="relative">
                  <i class="fas fa-lock absolute top-1/2 left-3 -translate-y-1/2 transform text-white/60"></i>
                  <Input
                    id="registerConfirmPassword"
                    aria-label="Confirm password"
                    autocomplete="new-password"
                    type="password"
                    placeholder="Confirm your password"
                    :disabled="awaitingLogin"
                    required
                    minlength="8"
                    v-model="registerConfirmPassword"
                    class="border-white/20 bg-white/10 pr-10 pl-10 text-white placeholder:text-white/60 focus:border-white focus:ring-white/20"
                  />
                </div>

                <!-- Terms checkbox -->
                <div class="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="terms"
                    class="mt-1 rounded-sm border-white/20 bg-white/10 text-white focus:ring-white/20"
                    required
                  />
                  <label for="terms" class="text-sm text-white/80">
                    I agree to the
                    <a href="/terms.html" target="_blank" class="text-white underline hover:text-white/80">
                      Terms & Conditions
                    </a>
                  </label>
                </div>

                <!-- Create Account Button -->
                <Button type="submit" class="login-submit w-full" :disabled="awaitingLogin"> Create account </Button>

                <!-- Divider -->
                <div class="relative my-6">
                  <div class="absolute inset-0 flex items-center">
                    <div class="w-full border-t border-white/20"></div>
                  </div>
                  <div class="relative flex justify-center text-sm">
                    <span class="form-divider px-2 text-white/60">Or register with</span>
                  </div>
                </div>

                <!-- Social Login Buttons -->
                <div class="w-full">
                  <Button
                    type="button"
                    variant="outline"
                    @click="googleAuth('register')"
                    :disabled="awaitingLogin"
                    class="w-full border-white/20 bg-white/10 py-3 text-white transition-all duration-200 hover:bg-white/20 hover:text-white"
                  >
                    <i class="fab fa-google"></i>
                    Google
                  </Button>
                </div>
              </div>
            </form>
          </TabsContent>

          <!-- Login Tab -->
          <TabsContent value="login" class="mt-4 space-y-4">
            <form @submit.prevent="emailAuth()">
              <Alert v-if="loginError" variant="destructive" class="mb-4 border-red-500/20 bg-red-900/20 text-red-400">
                <AlertTitle>Login Error</AlertTitle>
                <AlertDescription>
                  {{ loginError }}
                </AlertDescription>
              </Alert>

              <div class="space-y-4">
                <!-- Email -->
                <div class="relative">
                  <i class="fas fa-envelope absolute top-1/2 left-3 -translate-y-1/2 transform text-white/60"></i>
                  <Input
                    id="email"
                    aria-label="Email"
                    autocomplete="username"
                    type="email"
                    placeholder="Email"
                    :disabled="awaitingLogin || phase === 'auth'"
                    required
                    v-model="email"
                    class="border-white/20 bg-white/10 pl-10 text-white placeholder:text-white/60 focus:border-white focus:ring-white/20"
                  />
                </div>

                <!-- Password (shown in auth phase) -->
                <div v-if="phase === 'auth'">
                  <div class="relative">
                    <i class="fas fa-lock absolute top-1/2 left-3 -translate-y-1/2 transform text-white/60"></i>
                    <Input
                      id="password"
                      aria-label="Password"
                      autocomplete="current-password"
                      :type="showPassword ? 'text' : 'password'"
                      placeholder="Enter your password"
                      :disabled="awaitingLogin"
                      required
                      v-model="password"
                      class="border-white/20 bg-white/10 pr-10 pl-10 text-white placeholder:text-white/60 focus:border-white focus:ring-white/20"
                    />
                    <button
                      type="button"
                      class="absolute top-1/2 right-3 -translate-y-1/2 transform text-white/60 hover:text-white"
                      :aria-label="showPassword ? 'Hide password' : 'Show password'"
                      :aria-pressed="showPassword"
                      @click="showPassword = !showPassword"
                    >
                      <span class="text-xs font-semibold">{{ showPassword ? 'Hide' : 'Show' }}</span>
                    </button>
                  </div>
                </div>

                <!-- Login Button -->
                <Button type="submit" class="login-submit w-full" :disabled="awaitingLogin">
                  {{ phase === 'email' ? 'Continue' : 'Sign in' }}
                </Button>

                <template v-if="phase === 'auth'">
                  <!-- Reset Password -->
                  <div class="text-center">
                    <a
                      href="#"
                      @click.prevent="passwordReset"
                      class="text-sm text-white underline transition-colors duration-200 hover:text-white/80"
                      :class="{ 'pointer-events-none opacity-50': resettingPassword }"
                    >
                      <i v-if="resettingPassword" class="fa fa-spin fa-spinner-third mr-1"></i>
                      {{ resettingPassword ? 'Sending reset email...' : 'Reset password?' }}
                    </a>
                  </div>

                  <div class="relative my-6">
                    <div class="absolute inset-0 flex items-center">
                      <div class="w-full border-t border-white/20"></div>
                    </div>
                    <div class="relative flex justify-center text-sm">
                      <span class="form-divider px-2 text-white/60">Or continue with</span>
                    </div>
                  </div>

                  <!-- Google Auth (shown in auth phase) -->
                  <Button
                    type="button"
                    variant="outline"
                    @click="googleAuth('login')"
                    :disabled="awaitingLogin"
                    class="w-full border-white/20 bg-white/10 py-3 text-white transition-all duration-200 hover:bg-white/20 hover:text-white"
                  >
                    <i class="fab fa-google"></i>
                    Google
                  </Button>
                </template>
              </div>
            </form>
          </TabsContent>
        </Tabs>
        <p class="login-footer" data-sprite-exclusion>Tools for environmental compliance.</p>
      </section>
    </div>
  </main>
</template>

<style scoped>
.platform-login {
  --platform-background: var(--color-brand-blue);
  position: relative;
  isolation: isolate;
  overflow: clip;
  min-height: 100svh;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: clamp(24px, 3.5vw, 64px);
  background: var(--platform-background);
  color: #fff;
  font-family: system-ui, 'Segoe UI', sans-serif;
  -webkit-font-smoothing: antialiased;
}
.platform-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  width: 100%;
  max-width: 1800px;
  margin: 0 auto;
}
.toolkit-logo {
  display: block;
  flex-shrink: 0;
  width: 80px;
  height: auto;
  animation: logo-arrive 1.1s cubic-bezier(0.16, 1, 0.3, 1) backwards;
}
.platform-brand {
  display: flex;
  align-items: center;
  gap: 24px;
  min-width: 0;
}
.learn-more {
  flex-shrink: 0;
  color: #c3ccea;
  text-decoration: none;
  font-size: 16px;
  animation: content-arrive 900ms cubic-bezier(0.16, 1, 0.3, 1) 600ms backwards;
}
.learn-more span {
  margin-left: 8px;
}
.learn-more:hover {
  color: #fff;
}
.platform-body {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  grid-template-areas: 'orbit login';
  align-items: center;
  gap: clamp(20px, 3vw, 56px);
  row-gap: 24px;
  width: 100%;
  max-width: 1800px;
  flex: 1;
  margin: 32px auto 0;
}
.platform-scene {
  grid-area: orbit;
  min-width: 0;
  width: 100%;
  max-width: calc((100svh - 280px) * 1.785714);
  justify-self: center;
  transform: translateY(-4.5%);
}
.platform-headline {
  margin: 0;
  color: #f2f5ff;
  font-size: clamp(24px, 2vw, 34px);
  font-weight: 700;
  line-height: 1.2;
}
.platform-headline span {
  display: block;
  white-space: nowrap;
  animation: headline-arrive 1s cubic-bezier(0.16, 1, 0.3, 1) 120ms backwards;
}
.platform-headline span:nth-child(2) {
  animation-delay: 280ms;
}
.login-panel {
  --card-offset: 120px;
  grid-area: login;
  position: relative;
  width: 100%;
  padding: 28px;
  border: 1px solid rgb(158 180 244 / 24%);
  border-radius: 20px;
  background: color-mix(in srgb, var(--platform-background) 66%, transparent);
  backdrop-filter: blur(1px);
  box-shadow: 0 24px 64px -32px rgb(0 0 0 / 60%);
  scroll-margin-top: 24px;
  animation: card-arrive 1.15s cubic-bezier(0.16, 1, 0.3, 1) 700ms backwards;
}
.login-panel-header {
  text-align: center;
  margin-bottom: 24px;
  animation: content-arrive 850ms cubic-bezier(0.16, 1, 0.3, 1) 950ms backwards;
}
.login-tabs {
  animation: content-arrive 900ms cubic-bezier(0.16, 1, 0.3, 1) 1120ms backwards;
}
.active-tool-icon {
  display: block;
  width: 76px;
  height: 76px;
  margin: -4px auto 8px;
}
.active-tool-label {
  margin: 0 0 10px;
  color: #ffc400;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}
.login-panel h2 {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.15;
}
.login-subtitle {
  margin: 10px 0 0;
  color: #c3ccea;
  font-size: 15px;
  line-height: 1.45;
}
.login-footer {
  margin: 24px 0 0;
  color: #b8c5e3;
  text-align: center;
  font-size: 12px;
  line-height: 1.4;
  animation: content-arrive 850ms cubic-bezier(0.16, 1, 0.3, 1) 1320ms backwards;
}
@keyframes logo-arrive {
  from {
    opacity: 0;
    transform: translate(-48px, -32px) scale(0.2) rotate(-35deg);
  }
  65% {
    opacity: 1;
    transform: scale(1.12) rotate(5deg);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@keyframes headline-arrive {
  from {
    opacity: 0;
    transform: translateX(70px);
    filter: blur(12px);
  }
  to {
    opacity: 1;
    transform: none;
    filter: none;
  }
}
@keyframes card-arrive {
  from {
    opacity: 0;
    transform: translate(var(--card-offset), 48px) scale(0.8) rotate(5deg);
    filter: blur(12px);
  }
  75% {
    opacity: 1;
    transform: translateX(-6px) scale(1.015) rotate(-0.5deg);
    filter: blur(0);
  }
  to {
    opacity: 1;
    transform: none;
    filter: none;
  }
}
@keyframes content-arrive {
  from {
    opacity: 0;
    transform: translateY(24px);
    filter: blur(6px);
  }
  to {
    opacity: 1;
    transform: none;
    filter: none;
  }
}
.login-panel :deep(input:not([type='checkbox'])) {
  min-height: 44px;
  font-family: inherit;
  font-size: 16px;
}
.login-panel :deep(input:focus-visible),
.login-panel :deep(button:focus-visible),
.learn-more:focus-visible {
  outline: 2px solid #ffc400;
  outline-offset: 3px;
}
.login-panel :deep(button) {
  font-family: inherit;
  cursor: pointer;
}
.login-panel :deep(button:disabled) {
  cursor: wait;
}
.login-submit {
  min-height: 44px;
  border-radius: 8px;
  background: #ffc400;
  color: var(--color-brand-blue);
  font-size: 16px;
  font-weight: 700;
  box-shadow: none;
}
.login-submit:hover {
  background: #ffda59;
}
.form-divider {
  background: var(--platform-background);
}
.login-loading {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px;
  border-radius: inherit;
  background: color-mix(in srgb, var(--platform-background) 90%, transparent);
  backdrop-filter: blur(5px);
  text-align: center;
  color: #fff;
}
.loading-spinner {
  width: 28px;
  height: 28px;
  border: 2px solid rgb(255 255 255 / 20%);
  border-top-color: #ffc400;
  border-radius: 50%;
  animation: login-spin 1s linear infinite;
}
@keyframes login-spin {
  to {
    transform: rotate(360deg);
  }
}
@media (min-width: 1600px) {
  .platform-body {
    grid-template-columns: minmax(0, 1fr) 400px;
  }
  .login-panel {
    padding: 36px;
  }
}
@media (max-width: 1100px) {
  .platform-body {
    grid-template-columns: minmax(0, 1fr) 320px;
    gap: 24px;
  }
  .platform-scene {
    transform: none;
  }
  .login-panel {
    padding: 24px;
  }
}
@media (max-width: 680px) {
  .platform-login {
    padding: 24px 16px 32px;
  }
  .platform-header {
    gap: 12px;
    flex-wrap: wrap;
  }
  .platform-brand {
    gap: 12px;
  }
  .toolkit-logo {
    width: 50px;
  }
  .learn-more {
    margin-left: auto;
    font-size: 13px;
  }
  .learn-more span {
    margin-left: 2px;
  }
  .platform-body {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas: 'orbit' 'login';
    grid-template-rows: auto;
    flex: none;
    gap: 28px;
    margin-top: 28px;
  }
  .platform-scene {
    max-width: 900px;
    margin: 0 auto;
  }
  .platform-headline {
    font-size: clamp(16px, 3.8vw, 20px);
  }
  .login-panel {
    --card-offset: 32px;
    max-width: 420px;
    margin: 0 auto;
    padding: 24px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .toolkit-logo,
  .platform-headline span,
  .learn-more,
  .login-panel,
  .login-panel-header,
  .login-tabs,
  .login-footer,
  .loading-spinner {
    animation: none;
  }
}
</style>
